import type { Artwork } from '../types';
import type { AudioPlayer, AudioState } from './player';

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** 背面に回る/戻るときの短いフェード(ms)。急に止めると「ブツ」というポップ音が出る。 */
const PAUSE_FADE_MS = 60;

export class WebAudioPlayer implements AudioPlayer {
  private ctx: AudioContext | null = null;
  private gain: GainNode | null = null;
  private source: AudioBufferSourceNode | null = null;
  private readonly buffers = new Map<string, AudioBuffer>();
  private fadeToken = 0;
  private analyser: AnalyserNode | null = null;
  private probeTimer: ReturnType<typeof setInterval> | null = null;
  private startedAt = 0;
  state: AudioState = 'idle';
  current: Artwork | null = null;
  muted = false;

  constructor(private readonly fadeMs: number, private readonly log: (m: string) => void) {}

  /** 消音中は目標ゲインを 0 にする。 */
  private targetGain(volume: number): number {
    return this.muted ? 0 : volume;
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (!this.ctx || !this.gain) return;
    // 再生中だけゲインを動かす。フェードアウト中(follow の停止)はそのまま消えるに任せる。
    if (this.source && this.state === 'playing') this.ramp(this.targetGain(this.current?.volume_adjustment ?? 1));
  }

  unlock(): void {
    if (!this.ctx) {
      const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.ctx = new AC();
      this.gain = this.ctx.createGain();
      this.gain.gain.value = 0;
      this.gain.connect(this.ctx.destination);
      this.ctx.onstatechange = () => this.log(`audio state → ${this.ctx!.state} t=${this.ctx!.currentTime.toFixed(2)}`);
      this.log(`audio ctx created: state=${this.ctx.state} sr=${this.ctx.sampleRate}`);
    }
    // 操作ハンドラ内で無音を 1 サンプル鳴らす: iOS の古典的な unlock
    const b = this.ctx.createBuffer(1, 1, 22050);
    const s = this.ctx.createBufferSource();
    s.buffer = b;
    s.connect(this.ctx.destination);
    s.start();
    void this.ctx.resume().then(() => this.log(`audio resume → ${this.ctx!.state}`)).catch((e) => this.log(`audio resume failed: ${e}`));
  }

  private requireCtx(): AudioContext {
    if (!this.ctx) throw new Error('audio not unlocked');
    return this.ctx;
  }

  async preload(a: Artwork): Promise<void> {
    if (this.buffers.has(a.audio_asset)) return;
    const ctx = this.requireCtx();
    const res = await fetch(a.audio_asset);
    if (!res.ok) throw new Error(`audio fetch ${res.status}: ${a.audio_asset}`);
    this.buffers.set(a.audio_asset, await ctx.decodeAudioData(await res.arrayBuffer()));
    this.log(`audio ready: ${a.audio_asset}`);
  }

  private ramp(to: number, ms = this.fadeMs): void {
    const ctx = this.requireCtx();
    const g = this.gain!.gain;
    const t = ctx.currentTime;
    const from = g.value;
    g.cancelScheduledValues(t);
    g.setValueAtTime(from, t);
    g.linearRampToValueAtTime(to, t + ms / 1000);
    if (this.analyser) this.log(`gain ramp ${from.toFixed(3)} → ${to} in ${ms}ms at t=${t.toFixed(2)}`);
  }

  /** 再生中のバッファ内の位置(秒)。ループ境界との相関を見るための診断用。 */
  private loopPos(): number {
    const dur = this.source?.buffer?.duration ?? 0;
    if (!this.ctx || !dur) return -1;
    return (this.ctx.currentTime - this.startedAt) % dur;
  }

  /**
   * 診断用(?debug=1): 出力波形を監視し、隣接サンプル間の飛び(クリック)を検出してログに出す。
   * gain の後段で観測するので、ソースの stop / ループ境界 / ゲインの不連続はすべてここに現れる。
   */
  enableProbe(threshold = 0.6): void {
    const ctx = this.requireCtx();
    if (this.analyser) return;
    this.analyser = ctx.createAnalyser();
    this.analyser.fftSize = 2048;
    this.gain!.connect(this.analyser);
    const buf = new Float32Array(this.analyser.fftSize);
    let lastReport = 0;
    this.probeTimer = setInterval(() => {
      this.analyser!.getFloatTimeDomainData(buf);
      let maxStep = 0;
      let peak = 0;
      for (let i = 1; i < buf.length; i++) {
        const step = Math.abs(buf[i]! - buf[i - 1]!);
        if (step > maxStep) maxStep = step;
        const a = Math.abs(buf[i]!);
        if (a > peak) peak = a;
      }
      const t = ctx.currentTime;
      if (maxStep >= threshold && t - lastReport > 0.25) {
        lastReport = t;
        this.log(`⚠ click? step=${maxStep.toFixed(2)} peak=${peak.toFixed(2)} t=${t.toFixed(2)} gain=${this.gain!.gain.value.toFixed(2)} ${this.state}${this.muted ? ' muted' : ''} loop pos ${this.loopPos().toFixed(2)}s`);
      }
    }, 40);
    this.log(`audio probe on (threshold ${threshold})`);
  }

  async play(a: Artwork): Promise<void> {
    const ctx = this.requireCtx();
    if (this.source && this.current?.id === a.id) {
      // フェードアウト中の再認識: 打ち消して戻す
      this.fadeToken += 1;
      this.ramp(this.targetGain(a.volume_adjustment));
      this.state = 'playing';
      return;
    }
    this.stopNow();
    await this.preload(a);
    if (ctx.state !== 'running') await ctx.resume().catch(() => {});
    if (ctx.state !== 'running') {
      this.state = 'blocked';
      throw new Error(`audio context is ${ctx.state}`);
    }
    const src = ctx.createBufferSource();
    src.buffer = this.buffers.get(a.audio_asset)!;
    src.loop = true;
    src.connect(this.gain!);
    this.gain!.gain.setValueAtTime(0, ctx.currentTime);
    this.ramp(this.targetGain(a.volume_adjustment));
    src.start();
    this.startedAt = ctx.currentTime;
    this.source = src;
    this.current = a;
    this.state = 'playing';
    this.log(`audio start: ${a.title} state=${ctx.state} t=${ctx.currentTime.toFixed(2)}`);
    const t0 = ctx.currentTime;
    setTimeout(() => this.log(`audio +1s: state=${ctx.state} clock ${ctx.currentTime - t0 > 0.3 ? 'advancing' : 'FROZEN'} gain=${this.gain!.gain.value.toFixed(2)}`), 1000);
  }

  private stopNow(): void {
    // 保留中の stop() のフェードアウトタイマーを無効化する: 次に play() した新しい音を止めさせない。
    this.fadeToken += 1;
    if (!this.source) return;
    try { this.source.stop(); } catch { /* already stopped */ }
    this.source = null;
    this.current = null;
    this.state = 'idle';
  }

  async stop(): Promise<void> {
    if (!this.source) return;
    const token = ++this.fadeToken;
    this.state = 'fading';
    this.ramp(0);
    await sleep(this.fadeMs + 50);
    if (token === this.fadeToken) this.stopNow();
  }

  /** 背面に回るとき。ゲインを短く 0 へ落としてから suspend する(即座に止めるとポップ音)。 */
  async pause(): Promise<void> {
    if (!this.ctx || !this.gain) return;
    if (this.ctx.state === 'running' && this.gain.gain.value > 0) {
      this.ramp(0, PAUSE_FADE_MS);
      await sleep(PAUSE_FADE_MS + 10);
    }
    this.log(`audio pause: gain=${this.gain.gain.value.toFixed(2)} → suspend`);
    await this.ctx.suspend().catch(() => {});
  }

  /** 前面に戻るとき。resume 後に 0 から元の音量へ短く戻す(途中から急に鳴らさない)。 */
  async resume(): Promise<boolean> {
    if (!this.ctx) return false;
    await this.ctx.resume().catch(() => {});
    const running = this.ctx.state === 'running';
    if (running && this.source && this.state === 'playing') this.ramp(this.targetGain(this.current?.volume_adjustment ?? 1), PAUSE_FADE_MS);
    return running;
  }

  diagnostics(): string {
    if (!this.ctx) return 'audio: no ctx';
    return `audio ${this.ctx.state} t=${this.ctx.currentTime.toFixed(1)} gain=${this.gain!.gain.value.toFixed(2)} ${this.state}${this.muted ? ' muted' : ''}`;
  }
}
